/**
 * GRI Questionnaire API Service
 *
 * Provides API methods for GRI Standards questionnaires including:
 * - Standards and disclosures (reference data)
 * - Sessions (questionnaire instances per org/period)
 * - Responses (question answers with auto-save)
 * - Workflow (submit, approve, reject)
 * - Export (JSON, CSV)
 */

import { api } from './api';

// ==================== TYPES ====================

export type GRICategory = 'environment' | 'social' | 'governance';
export type SessionStatus = 'draft' | 'submitted' | 'approved' | 'rejected';
export type QuestionType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'percentage'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'file'
  | 'table';

export interface GRIStandard {
  id: string;
  code: string;
  name: string;
  version_year: number;
  category: GRICategory;
  description?: string;
  disclosure_count: number;
}

export interface GRIDisclosure {
  id: string;
  standard_id: string;
  code: string;
  title: string;
  description?: string;
  guidance?: string;
  display_order: number;
  question_count: number;
}

export interface QuestionOption {
  value: string;
  label: string;
}

export interface GRIQuestion {
  id: string;
  disclosure_id: string;
  question_text: string;
  question_type: QuestionType;
  help_text?: string;
  placeholder?: string;
  is_required: boolean;
  validation_rules?: Record<string, unknown>;
  options?: QuestionOption[];
  unit?: string;
  display_order: number;
  current_response?: GRIResponseValue;
}

export interface GRIResponseValue {
  id?: string;
  text_value?: string;
  number_value?: number;
  boolean_value?: boolean;
  date_value?: string;
  json_value?: unknown;
  attachment_ids?: string[];
  last_modified_by?: string;
  updated_at?: string;
}

export interface GRISession {
  id: string;
  category: GRICategory;
  reporting_period: string;
  status: SessionStatus;
  total_questions: number;
  answered_questions: number;
  completion_percentage: number;
  notes?: string;
  created_by?: string;
  created_at?: string;
  submitted_at?: string;
  submitted_by?: string;
  approved_at?: string;
  approved_by?: string;
  rejection_reason?: string;
}

export interface StandardProgress {
  standard_id: string;
  standard_code: string;
  standard_name: string;
  total: number;
  answered: number;
  percentage: number;
}

export interface SessionProgress {
  session_id: string;
  category: string;
  reporting_period: string;
  status: SessionStatus;
  total_questions: number;
  answered_questions: number;
  completion_percentage: number;
  standards_progress: StandardProgress[];
}

export interface ResponseUpdate {
  question_id: string;
  text_value?: string;
  number_value?: number;
  boolean_value?: boolean;
  date_value?: string;
  json_value?: unknown;
}

export interface SessionProgressUpdate {
  answered_questions: number;
  completion_percentage: number;
}

// ==================== API RESPONSES ====================

interface StandardsResponse {
  standards: GRIStandard[];
}

interface DisclosuresResponse {
  disclosures: GRIDisclosure[];
}

interface QuestionsResponse {
  questions: GRIQuestion[];
}

interface SessionCreateResponse {
  session_id: string;
  existing: boolean;
  status: SessionStatus;
  completion_percentage: number;
}

interface SessionsListResponse {
  sessions: GRISession[];
}

interface ResponseSaveResult {
  saved: boolean;
  response_id: string | null;
  session_progress: SessionProgressUpdate;
}

interface BulkResponseSaveResult {
  saved: boolean;
  saved_count: number;
  session_progress: SessionProgressUpdate;
}

interface WorkflowResult {
  session_id: string;
  status: SessionStatus;
  submitted_at?: string;
  approved_at?: string;
  reason?: string;
}

interface ExportResponse {
  session: {
    id: string;
    category: string;
    reporting_period: string;
    status: SessionStatus;
    completion_percentage: number;
    exported_at: string;
  };
  responses: Array<{
    standard_code: string;
    standard_name: string;
    disclosure_code: string;
    disclosure_title: string;
    question: string;
    question_type: string;
    unit?: string;
    response: unknown;
    last_modified_by: string;
    last_modified_at: string;
  }>;
}

// ==================== SERVICE ====================

export const griService = {
  /**
   * Standards - Reference data endpoints
   */
  standards: {
    /**
     * List all GRI standards, optionally filtered by category
     */
    list: async (category?: GRICategory): Promise<GRIStandard[]> => {
      const query = category ? `?category=${category}` : '';
      const response = await api.get<StandardsResponse>(`/gri/standards${query}`);
      return response.standards;
    },

    /**
     * Get all disclosures for a specific standard
     */
    getDisclosures: async (standardId: string): Promise<GRIDisclosure[]> => {
      const response = await api.get<DisclosuresResponse>(
        `/gri/standards/${standardId}/disclosures`
      );
      return response.disclosures;
    },
  },

  /**
   * Questions - Get questions for a disclosure
   */
  questions: {
    /**
     * List all questions for a disclosure with optional current responses
     */
    list: async (disclosureId: string, sessionId?: string): Promise<GRIQuestion[]> => {
      const query = sessionId ? `?session_id=${sessionId}` : '';
      const response = await api.get<QuestionsResponse>(
        `/gri/disclosures/${disclosureId}/questions${query}`
      );
      return response.questions;
    },
  },

  /**
   * Sessions - Questionnaire session management
   */
  sessions: {
    /**
     * Create a new session or get existing one for category/period
     */
    create: async (
      category: GRICategory,
      reporting_period: string
    ): Promise<SessionCreateResponse> => {
      return api.post<SessionCreateResponse>('/gri/sessions', {
        category,
        reporting_period,
      });
    },

    /**
     * List all sessions for the organization
     */
    list: async (params?: {
      category?: GRICategory;
      period?: string;
      status?: SessionStatus;
    }): Promise<GRISession[]> => {
      const query = new URLSearchParams();
      if (params?.category) query.append('category', params.category);
      if (params?.period) query.append('period', params.period);
      if (params?.status) query.append('status', params.status);
      const queryString = query.toString();
      const response = await api.get<SessionsListResponse>(
        `/gri/sessions${queryString ? `?${queryString}` : ''}`
      );
      return response.sessions;
    },

    /**
     * Get session details
     */
    get: async (sessionId: string): Promise<GRISession> => {
      return api.get<GRISession>(`/gri/sessions/${sessionId}`);
    },

    /**
     * Get detailed progress breakdown by standard
     */
    getProgress: async (sessionId: string): Promise<SessionProgress> => {
      return api.get<SessionProgress>(`/gri/sessions/${sessionId}/progress`);
    },

    /**
     * Submit session for approval
     */
    submit: async (sessionId: string, notes?: string): Promise<WorkflowResult> => {
      return api.post<WorkflowResult>(`/gri/sessions/${sessionId}/submit`, {
        notes,
      });
    },

    /**
     * Approve a submitted session (admin/manager only)
     */
    approve: async (sessionId: string, comment?: string): Promise<WorkflowResult> => {
      return api.post<WorkflowResult>(`/gri/sessions/${sessionId}/approve`, {
        comment,
      });
    },

    /**
     * Reject a submitted session (admin/manager only)
     */
    reject: async (sessionId: string, reason: string): Promise<WorkflowResult> => {
      return api.post<WorkflowResult>(`/gri/sessions/${sessionId}/reject`, {
        reason,
      });
    },

    /**
     * Export session responses
     */
    export: async (
      sessionId: string,
      format: 'json' | 'csv' | 'xlsx' = 'xlsx'
    ): Promise<ExportResponse | Blob> => {
      if (format === 'csv' || format === 'xlsx') {
        // Use the api.getBlob method which handles auth correctly
        const result = await api.getBlob(`/gri/sessions/${sessionId}/export?format=${format}`);
        return result.blob;
      }
      return api.get<ExportResponse>(
        `/gri/sessions/${sessionId}/export?format=${format}`
      );
    },

    /**
     * Download Excel export
     */
    downloadExcel: async (sessionId: string, filename?: string): Promise<void> => {
      const result = await api.getBlob(`/gri/sessions/${sessionId}/export?format=xlsx`);

      // Verify we got a valid blob with content
      if (result.blob.size === 0) {
        throw new Error('Export failed: Empty response');
      }

      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || result.filename || `gri-export-${sessionId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  },

  /**
   * Responses - Save question answers
   */
  responses: {
    /**
     * Save or update a single response (auto-save)
     */
    save: async (
      sessionId: string,
      response: ResponseUpdate
    ): Promise<ResponseSaveResult> => {
      return api.put<ResponseSaveResult>(
        `/gri/sessions/${sessionId}/responses`,
        response
      );
    },

    /**
     * Save multiple responses at once
     */
    saveBulk: async (
      sessionId: string,
      responses: ResponseUpdate[]
    ): Promise<BulkResponseSaveResult> => {
      return api.put<BulkResponseSaveResult>(
        `/gri/sessions/${sessionId}/responses/bulk`,
        { responses }
      );
    },
  },
};

export default griService;
